import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanBarcode, X, Loader2, Plus } from "lucide-react";
import api from "../lib/api";

// Camera barcode scanner (EAN/UPC via html5-qrcode) → product lookup → onFound(name).
export default function BarcodeScanner({ onFound, testPrefix = "barcode" }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle");
  const [hit, setHit] = useState(null);
  const [manual, setManual] = useState("");
  const [useBy, setUseBy] = useState("");
  const quick = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setUseBy(d.toISOString().slice(0, 10)); };
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        if (cancelled) return;
        const s = new Html5Qrcode("sorted-barcode-view", { formatsToSupport: [Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8, Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.UPC_E, Html5QrcodeSupportedFormats.CODE_128], verbose: false });
        scannerRef.current = s;
        setState("scanning");
        await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 260, height: 140 } }, async (code) => {
          if (state === "looking") return;
          try { await s.pause(true); } catch { /* ignore */ }
          await lookup(code);
        }, () => {});
      } catch (e) {
        setState("error");
        toast.error("Couldn't open the camera — allow camera access and try again");
      }
    })();
    return () => { cancelled = true; const s = scannerRef.current; scannerRef.current = null; if (s) { s.stop().then(() => s.clear()).catch(() => {}); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lookup = async (code) => {
    setState("looking");
    try {
      const { data } = await api.get(`/barcode/${encodeURIComponent(code)}`);
      setHit({ code, name: data.name || "", found: !!data.name, brand: data.brand || "" });
      setManual(data.name || "");
    } catch { setHit({ code, name: "", found: false }); }
    setState("result");
  };

  const add = () => {
    const name = manual.trim();
    if (!name) return toast.error("Give it a name so Millie knows what it is");
    onFound(name, useBy || null);
    toast.success(useBy ? `${name} added — I'll remind you before it's off` : `${name} added to your kitchen`);
    again();
  };
  const again = async () => { setHit(null); setManual(""); setUseBy(""); setState("scanning"); busyRef.current = false; try { await scannerRef.current?.resume(); } catch { /* ignore */ } };
  const close = () => { setOpen(false); setHit(null); setState("idle"); };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${testPrefix}-open`}
        className="w-12 h-12 rounded-full bg-white border-2 border-[#FDECEF] text-sorted-ink flex items-center justify-center shrink-0" aria-label="Scan a barcode">
        <ScanBarcode size={20} strokeWidth={2.5} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-center" data-testid={`${testPrefix}-sheet`}>
          <div className="w-full max-w-md bg-[#FFF6F1] rounded-t-[32px] p-5 pb-8">
            <div className="flex items-center justify-between">
              <h3 className="font-hand text-3xl text-sorted-ink">Scan a barcode</h3>
              <button type="button" onClick={close} data-testid={`${testPrefix}-close`} className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-ink"><X size={17} strokeWidth={2.5} /></button>
            </div>
            <p className="text-xs font-semibold text-sorted-slate mt-1">Point the camera at the barcode on a packet, tin or jar.</p>
            <div id="sorted-barcode-view" className="mt-3 rounded-3xl overflow-hidden bg-black min-h-[220px]" data-testid={`${testPrefix}-view`} />
            {state === "looking" && <p className="mt-3 text-sm font-bold text-sorted-ink flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Looking that up…</p>}
            {state === "result" && hit && (
              <div className="mt-3 bg-white rounded-3xl border-2 border-[#FDECEF] p-3" data-testid={`${testPrefix}-result`}>
                <p className="text-[11px] font-bold text-sorted-slate">{hit.found ? `Found it${hit.brand ? ` · ${hit.brand}` : ""}` : `Barcode ${hit.code} isn't in the database yet — just type what it is`}</p>
                <div className="flex items-center gap-2 mt-2">
                  <input value={manual} onChange={(e) => setManual(e.target.value)} data-testid={`${testPrefix}-name`} placeholder="e.g. Tinned tomatoes"
                    className="flex-1 bg-[#FFF6F1] border-2 border-[#FDECEF] rounded-2xl px-3 py-2.5 text-sm font-semibold text-sorted-ink outline-none focus:border-sorted-rose" />
                  <button type="button" onClick={add} data-testid={`${testPrefix}-add`} className="h-10 px-4 rounded-full bg-sorted-rose text-white text-sm font-bold flex items-center gap-1"><Plus size={15} strokeWidth={3} /> Add</button>
                </div>
                <div className="mt-3">
                  <p className="text-[11px] font-bold text-sorted-slate">Use-by date (optional — for fridge alerts)</p>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="date" value={useBy} onChange={(e) => setUseBy(e.target.value)} data-testid={`${testPrefix}-useby`}
                      className="flex-1 bg-[#FFF6F1] border-2 border-[#FDECEF] rounded-2xl px-3 py-2 text-sm font-semibold text-sorted-ink outline-none focus:border-sorted-rose" />
                    {[["3d", 3], ["1w", 7], ["1m", 30]].map(([l, d]) => (
                      <button key={l} type="button" onClick={() => quick(d)} data-testid={`${testPrefix}-useby-${l}`} className="px-2.5 py-2 rounded-full text-[11px] font-bold bg-white border-2 border-[#FDECEF] text-sorted-slate">+{l}</button>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={again} data-testid={`${testPrefix}-again`} className="mt-2 text-[11px] font-bold text-sorted-slate">Scan another instead</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
